import * as Icons from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ClosingCTA } from "@/pages/Home";
import { BidderStream } from "@/components/mocks/ProductMocks";

const Icon = ({ name, className = "w-6 h-6" }) => {
  const C = Icons[name] || Icons.Box;
  return <C className={className} aria-hidden="true" />;
};

const apiSnippet = `POST /v2/campaigns HTTP/1.1
Authorization: Bearer <your-api-key>
Content-Type: application/json

{
  "name": "Q3 Omnichannel Launch",
  "outcome_goal": { "type": "ROAS", "target": 4.0 },
  "channels": ["ctv", "display", "audio", "dooh"],
  "budget": { "amount": 250000, "pacing": "even" },
  "fee_disclosure": true
}

# 201 Created
# -> log-level exports enabled by default`;

export default function Platform() {
  const { capabilities } = useContent();
  return (
    <>
      <section className="border-b border-[#D8E2E6]" data-testid="platform-hero">
        <div className="container-riq pt-16 pb-20 lg:pt-24 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-5">Platform</p>
            <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.1}>One platform.</MaskedLine>
              <MaskedLine delay={0.22}>
                Zero <span className="accent-word">blind spots.</span>
              </MaskedLine>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#4A5A63] leading-relaxed max-w-xl">
              Campaign management, RTB, outcome AI, supply, safety, creative, reporting, measurement and identity — engineered as one system, not nine acquisitions bolted together.
            </p>
          </div>
          <div className="lg:col-span-5">
            <BidderStream />
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="capability-grid">
        <div className="container-riq">
          <Reveal>
            <p className="eyebrow mb-3">Capabilities</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl">
              Nine disciplines. <span className="accent-word">One</span> seat.
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((c, i) => (
              <Reveal key={c.id} delay={(i % 3) * 0.08}>
                <article className="card-brut-sm card-hover p-6 h-full" data-testid={`capability-${c.id}`}>
                  <div className="w-11 h-11 border border-[#D8E2E6] bg-[#E4F3F7] flex items-center justify-center text-[#0E7A8F]">
                    <Icon name={c.icon} />
                  </div>
                  <h3 className="font-display font-bold text-xl tracking-tight mt-4">{c.name}</h3>
                  <p className="text-sm text-[#4A5A63] mt-2 leading-relaxed">{c.desc}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 panel-dark border-y border-[#D8E2E6]" data-testid="open-standards">
        <div className="container-riq grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow mb-3">Open standards & API</p>
            <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl lg:text-4xl text-[#F7F9FA]">
              Built to be <span className="accent-word">queried.</span>
            </h2>
            <p className="mt-5 text-[#F7F9FA]/70 leading-relaxed">
              Full REST API for campaigns, reporting and log exports. OpenRTB-native supply, webhook event streams, and standard export formats (CSV, Parquet, BigQuery transfer). If it can be automated, it can be automated against RresponsIQ.
            </p>
            <ul className="mt-6 space-y-2.5 font-mono2 text-sm text-[#F7F9FA]/80">
              {["OpenRTB 2.6 native", "REST + webhooks, full CRUD", "Raw logs → S3 / GCS / BigQuery", "ads.txt / sellers.json enforced"].map((f) => (
                <li key={f} className="flex items-center gap-2.5">
                  <Icons.Check className="w-4 h-4 text-[#32A2C5] shrink-0" aria-hidden="true" /> {f}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mock-frame" data-testid="api-snippet">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F7F9FA]/20 bg-[#17171B]">
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#F7F9FA]/70">api.responsiq.com</p>
                <span className="font-mono2 text-[10px] font-bold px-2 py-0.5 rounded bg-[#32A2C5] text-[#101820]">REST v2</span>
              </div>
              <pre className="p-5 font-mono2 text-[11px] leading-relaxed text-[#F7F9FA]/85 overflow-x-auto">{apiSnippet}</pre>
            </div>
          </Reveal>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
